import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from 'components/UnvUI'

import PlagiarismService from 'service/PlagiarismService'
import Notification from 'utils/Notifications'

import CheckRow from './components/CheckRow/CheckRow'
import MatchesList from './components/MatchesList/MatchesList'

import styles from './PlagiarismPage.module.scss'

const fmtDate = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

const PlagiarismPage = () => {
  const { t } = useTranslation('common')
  const [checks, setChecks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [details, setDetails] = useState(null)
  const [runningId, setRunningId] = useState(null)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const load = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await PlagiarismService.listChecks()
      setChecks(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setChecks([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleRun = async (check) => {
    setRunningId(check.id)
    try {
      const res = await PlagiarismService.runCheck(check.id)
      setNotify({ isOpen: true, message: t('plagiarism.check_completed'), type: 'success' })
      setChecks((prev) => prev.map((c) => (c.id === check.id ? res.data : c)))
    } catch (e) {
      setNotify({
        isOpen: true,
        message: e.response?.data?.detail || t('plagiarism.check_error'),
        type: 'error',
      })
    } finally {
      setRunningId(null)
    }
  }

  const handleView = async (check) => {
    try {
      const res = await PlagiarismService.getCheck(check.id)
      setDetails(res.data)
    } catch {
      setDetails(check)
    }
  }

  const detailsMatches = details?.source_links || details?.matches || []
  const detailsSimilarity =
    details?.percent_plagiarism != null
      ? Number(details.percent_plagiarism)
      : details?.borrowings != null
      ? Number(details.borrowings)
      : null
  const detailsUnique =
    details?.percent_unique != null
      ? Number(details.percent_unique)
      : details?.originality != null
      ? Number(details.originality)
      : null

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('plagiarism.antiplagiat_title')}</h1>
          <div className={styles.subtitle}>
            {t('plagiarism.heuristic_subtitle')}
          </div>
        </div>
        <button className={styles.refresh} onClick={load} disabled={loading}>
          {t('common.refresh')}
        </button>
      </div>

      <div className={styles.list}>
        {loading ? (
          <div className={styles.loaderWrap}>
            <Skeleton variant="table" rows={5} cols={4} />
          </div>
        ) : error ? (
          <div className={styles.empty}>{t('plagiarism.load_error')}</div>
        ) : checks.length === 0 ? (
          <div className={styles.empty}>{t('plagiarism.no_checks')}</div>
        ) : (
          checks.map((c) => (
            <CheckRow
              key={c.id}
              check={c}
              running={runningId === c.id}
              onRun={handleRun}
              onView={handleView}
            />
          ))
        )}
      </div>

      {details && (
        <div className={styles.modalBackdrop} onClick={() => setDetails(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {details.file_name || t('plagiarism.check_label', { id: details.id })}
              </h2>
              <button className={styles.closeBtn} onClick={() => setDetails(null)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.statsGrid}>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>{t('plagiarism.stat_matches')}</div>
                  <div className={styles.statValueWarn}>
                    {detailsSimilarity != null
                      ? `${detailsSimilarity.toFixed(1)}%`
                      : '—'}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>{t('plagiarism.stat_originality')}</div>
                  <div className={styles.statValueOk}>
                    {detailsUnique != null
                      ? `${detailsUnique.toFixed(1)}%`
                      : '—'}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>{t('plagiarism.stat_provider')}</div>
                  <div className={styles.statValue}>{details.provider || '—'}</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>{t('plagiarism.stat_completed')}</div>
                  <div className={styles.statValue}>
                    {fmtDate(details.completed_at)}
                  </div>
                </div>
              </div>

              <h3 className={styles.matchesHead}>{t('plagiarism.found_matches_title')}</h3>
              <MatchesList matches={detailsMatches} />
            </div>
          </div>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default PlagiarismPage
